
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/HeaderUpdated';
import Footer from '@/components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { MapPin, ShoppingCart, AlertTriangle } from 'lucide-react';
import ProductBuyButton from '@/components/shop/ProductBuyButton';
import { useLanguage } from '@/contexts/LanguageContext';

interface Store {
  id: string;
  name: string;
  location: string;
}

interface UserStore {
  id: string;
  store_id: string;
  is_default: boolean;
}

const Checkout = () => {
  const navigate = useNavigate();
  const { cart, getTotalPoints, removeFromCart } = useCart();
  const { user, userProfile } = useAuth();
  const { t } = useLanguage();
  const [userStores, setUserStores] = useState<UserStore[]>([]);
  const [availableStores, setAvailableStores] = useState<Store[]>([]);
  const [loadingStores, setLoadingStores] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }

    if (cart.length === 0) {
      navigate('/shop');
      return;
    }

    loadStores();
  }, [user, cart, navigate]);

  const loadStores = async () => {
    if (!user) return;

    try {
      setLoadingStores(true);

      // Carregar lojas do usuário
      const { data: userStoresData, error: userStoresError } = await supabase
        .from('user_stores')
        .select('id, store_id, is_default')
        .eq('user_id', user.id);

      if (userStoresError) throw userStoresError;

      // Carregar todas as lojas disponíveis
      const { data: allStoresData, error: allStoresError } = await supabase
        .from('stores')
        .select('id, name, location');

      if (allStoresError) throw allStoresError;

      setUserStores(userStoresData || []);
      setAvailableStores(allStoresData || []);

    } catch (error) {
      console.error('Erro ao carregar lojas:', error);
      toast.error('Erro ao carregar lojas disponíveis');
    } finally {
      setLoadingStores(false);
    }
  };

  const getStoreName = (storeId: string) => {
    const store = availableStores.find(s => s.id === storeId);
    return store?.name || "Loja não encontrada";
  };

  const getStoreLocation = (storeId: string) => {
    const store = availableStores.find(s => s.id === storeId);
    return store?.location || "";
  };

  const handleProductSuccess = (productId: string) => {
    // Remove o produto do carrinho após troca bem-sucedida
    removeFromCart(productId);
    
    // Se não houver mais produtos no carrinho, redireciona para a loja
    if (cart.length <= 1) {
      toast.success("Todas as trocas foram realizadas!");
      navigate('/shop');
    }
  };

  const totalPoints = getTotalPoints();
  const userPoints = userProfile?.points || 0;

  if (cart.length === 0) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col bg-brand-darker text-white">
      <Header />
      <main className="flex-grow py-12 px-4">
        <div className="container mx-auto max-w-4xl">
          <h1 className="text-3xl font-bold mb-8 text-center">Finalizar Compra</h1>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Lista de Produtos */}
            <div className="lg:col-span-2 space-y-4">
              <Card className="bg-brand-gray border-none text-white">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ShoppingCart size={20} />
                    Produtos no Carrinho
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {cart.map((item) => (
                    <div key={item.productId} className="bg-brand-dark rounded-lg p-6">
                      <div className="flex items-start gap-4 mb-4">
                        <div className="w-20 h-20 bg-white rounded flex-shrink-0">
                          <img 
                            src={item.product.image} 
                            alt={item.product.name} 
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <div className="flex-grow">
                          <h3 className="font-medium text-lg">{item.product.name}</h3>
                          <p className="text-sm text-gray-400 mt-1">
                            Quantidade: {item.quantity}
                          </p>
                          <p className="text-brand-red font-bold text-lg mt-2">
                            {item.product.pointsPrice * item.quantity} pontos
                          </p>
                        </div>
                      </div>
                      
                      {/* Botão de troca individual para cada produto */}
                      <div className="flex justify-end">
                        <ProductBuyButton
                          product={item.product}
                          quantity={item.quantity}
                          className="bg-brand-red hover:bg-brand-red/90 px-6"
                          onSuccess={() => handleProductSuccess(item.productId)}
                        />
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>

            {/* Informações Laterais */}
            <div className="space-y-6">
              {/* Saldo de pontos */}
              <Card className="bg-brand-gray border-none text-white">
                <CardHeader>
                  <CardTitle className="text-lg">Seus Pontos</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-brand-red mb-2">
                      {userPoints}
                    </div>
                    <p className="text-sm text-gray-400">pontos disponíveis</p>
                  </div>
                </CardContent>
              </Card>

              {/* Resumo do carrinho */}
              <Card className="bg-brand-gray border-none text-white">
                <CardHeader>
                  <CardTitle className="text-lg">Resumo</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Total de produtos:</span>
                      <span>{cart.length}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Total de itens:</span>
                      <span>{cart.reduce((sum, item) => sum + item.quantity, 0)}</span>
                    </div>
                    <div className="border-t border-gray-700 pt-2">
                      <div className="flex justify-between font-bold">
                        <span>Total pontos:</span>
                        <span className="text-brand-red">{totalPoints}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Status da conexão */}
              {!userProfile?.connectedAccountId && (
                <Card className="bg-orange-500/10 border border-orange-500/30 text-white">
                  <CardContent className="p-4">
                    <div className="flex gap-2">
                      <AlertTriangle className="text-orange-400 flex-shrink-0" size={20} />
                      <div>
                        <p className="text-sm text-orange-400 font-medium mb-2">
                          Conta DartsBeat não conectada
                        </p>
                        <p className="text-xs text-orange-300">
                          Conecte sua conta para trocar pontos por produtos
                        </p>
                        <Button 
                          size="sm" 
                          className="mt-2 bg-orange-600 hover:bg-orange-700"
                          onClick={() => navigate('/my-account')}
                        >
                          Conectar Conta
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Status das lojas */}
              {userStores.length === 0 && (
                <Card className="bg-yellow-500/10 border border-yellow-500/30 text-white">
                  <CardContent className="p-4">
                    <div className="flex gap-2">
                      <AlertTriangle className="text-yellow-400 flex-shrink-0" size={20} />
                      <div>
                        <p className="text-sm text-yellow-400 font-medium mb-2">
                          Nenhuma loja selecionada
                        </p>
                        <p className="text-xs text-yellow-300">
                          Selecione uma loja para retirada dos produtos
                        </p>
                        <Button 
                          size="sm" 
                          className="mt-2 bg-yellow-600 hover:bg-yellow-700"
                          onClick={() => navigate('/my-account?tab=stores')}
                        >
                          Selecionar Lojas
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          {/* Botão para voltar às compras */}
          <div className="text-center mt-8">
            <Button
              variant="outline"
              onClick={() => navigate('/shop')}
              className="bg-transparent border-gray-700 text-white hover:bg-gray-700"
            >
              {t('checkout.continueShopping')}
            </Button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Checkout;
